/**
 * PhotoCollection2 - Core photo collection component
 *
 * Orchestrates multiple PhotoItem2 components with layout and selection management.
 *
 * @module PhotoCollection2
 */

import { useCallback, useRef, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { layouts } from './layouts';
import { ErrorState } from '../composed/ErrorState';
import { Text } from '../atoms/Text';
import { cn } from '@/lib/utils';
import { usePhotoReorder } from './hooks/usePhotoReorder';
import type { PhotoCollection2Props } from './PhotoCollection2.types';
import logger from '@/utils/logger';

// Layouts that support scrollable header/footer slots (#102)
// Horizontal layout scrolls on X-axis, so vertical header/footer slots don't make sense
const SLOT_SUPPORTED_LAYOUTS = ['grid', 'magazine', 'vertical'];

/**
 * PhotoCollection2 component
 *
 * Manages photo collection display with:
 * - Layout selection (grid, magazine)
 * - Selection state (none, single, multiple)
 * - Zoom control via CSS custom properties
 * - Prop inheritance to child items
 *
 * @example
 * ```typescript
 * <PhotoCollection2
 *   photos={photos}
 *   layout="grid"
 *   itemTemplate="photo-only"
 *   zoom={1.0}
 *   selectionMode="multiple"
 *   selectedIds={['photo-1']}
 *   onSelectionChange={setSelectedIds}
 * />
 * ```
 */
export function PhotoCollection2({
  photos,
  description,
  layout = 'grid',
  itemTemplate = 'photo-only',
  zoom = 1.0,
  selectionMode = 'none',
  selectedIds = [],
  onSelectionChange,
  onCritiqueClick,
  reorderMode = false,
  onReorder,
  className,
  // Header & Footer slots (#102)
  fixedHeader,
  scrollHeader,
  scrollFooter,
  fixedFooter,
}: PhotoCollection2Props) {
  // Track last clicked index for shift+click range selection
  const lastClickedIndexRef = useRef<number | null>(null);

  // Track whether we've already warned about missing onReorder callback
  const hasWarnedRef = useRef(false);

  // Track whether we've already warned about unsupported slots (#102)
  const hasWarnedSlotsRef = useRef(false);

  const supportsScrollSlots = SLOT_SUPPORTED_LAYOUTS.includes(layout);

  // Filter out slots for unsupported layouts, warn once (#102)
  const hasScrollSlots = !!(scrollHeader || scrollFooter);
  useEffect(() => {
    if (hasScrollSlots && !supportsScrollSlots && !hasWarnedSlotsRef.current) {
      logger.warn(
        'PhotoCollection2',
        `Layout "${layout}" does not support scrollable header/footer slots. Scroll slots will be ignored. Fixed slots will still render. Supported layouts: ${SLOT_SUPPORTED_LAYOUTS.join(', ')}`
      );
      hasWarnedSlotsRef.current = true;
    }
  }, [hasScrollSlots, supportsScrollSlots, layout]);

  // Apply slot filtering - only render slots for supported layouts
  const effectiveFixedHeader = fixedHeader;
  const effectiveScrollHeader = supportsScrollSlots ? scrollHeader : undefined;
  const effectiveScrollFooter = supportsScrollSlots ? scrollFooter : undefined;
  const effectiveFixedFooter = fixedFooter;

  // Reorder mode validation (warn only once)
  useEffect(() => {
    if (reorderMode && !onReorder && !hasWarnedRef.current) {
      logger.warn('PhotoCollection2', '[PhotoCollection2] Reorder mode enabled but onReorder callback missing');
      hasWarnedRef.current = true;
    }
  }, [reorderMode, onReorder]);

  // Initialize reorder hook
  const { reorderContext, handlers } = usePhotoReorder({
    items: photos,
    onReorder,
    enabled: reorderMode,
  });

  // Initialize DnD Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts (prevents accidental drags)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Selection change handler with shift+click range selection
  // IMPORTANT: This hook must be called unconditionally (before any returns)
  const handleItemClick = useCallback((photoId: string, event?: React.MouseEvent) => {
    if (selectionMode === 'none' || !onSelectionChange) return;

    const clickedIndex = photos.findIndex(p => p.photoData.id === photoId);

    if (selectionMode === 'single') {
      // Single mode: toggle if clicking same item, otherwise replace selection
      const isCurrentlySelected = selectedIds.length === 1 && selectedIds[0] === photoId;
      onSelectionChange(isCurrentlySelected ? [] : [photoId]);
      lastClickedIndexRef.current = clickedIndex;
    } else if (selectionMode === 'always-one') {
      // Always-one mode: always keep exactly one item selected
      // Clicking selected item does nothing, clicking different item switches
      const isCurrentlySelected = selectedIds.length === 1 && selectedIds[0] === photoId;
      if (!isCurrentlySelected) {
        onSelectionChange([photoId]);
        lastClickedIndexRef.current = clickedIndex;
      }
      // If clicking already selected item, do nothing (don't call onSelectionChange)
    } else if (selectionMode === 'multiple') {
      // Multiple mode with shift+click range selection
      if (event?.shiftKey && selectedIds.length > 0) {
        // Shift+click: select range from last clicked (or last selected) to current
        let rangeStart = lastClickedIndexRef.current;

        // If no last clicked index, find the last selected photo's index
        if (rangeStart === null) {
          const lastSelectedId = selectedIds[selectedIds.length - 1];
          rangeStart = photos.findIndex(p => p.photoData.id === lastSelectedId);
        }

        // Only proceed if we have a valid range start index (not -1 from findIndex)
        if (rangeStart !== null && rangeStart !== -1 && rangeStart !== clickedIndex) {
          const startIndex = Math.min(rangeStart, clickedIndex);
          const endIndex = Math.max(rangeStart, clickedIndex);

          // Get all photo IDs in the range
          const rangeIds = photos.slice(startIndex, endIndex + 1).map(p => p.photoData.id);

          // Combine with existing selection (union)
          const newSelection = Array.from(new Set([...selectedIds, ...rangeIds]));
          onSelectionChange(newSelection);
          // Don't update lastClickedIndexRef on shift+click to allow chaining
          return;
        }
      }

      // Normal click: toggle membership
      const isCurrentlySelected = selectedIds.includes(photoId);
      const newSelection = isCurrentlySelected
        ? selectedIds.filter(id => id !== photoId)
        : [...selectedIds, photoId];
      onSelectionChange(newSelection);
      lastClickedIndexRef.current = clickedIndex;
    }
  }, [selectionMode, selectedIds, onSelectionChange, photos]);

  // Adjust selection state when selection mode changes
  // This ensures the selection is valid for the current mode
  useEffect(() => {
    if (!onSelectionChange) return;

    if (selectionMode === 'none') {
      // Clear all selections when switching to none mode
      if (selectedIds.length > 0) {
        onSelectionChange([]);
      }
    } else if (selectionMode === 'always-one') {
      // Always-one mode: ensure exactly one item is selected
      if (selectedIds.length === 0 && photos.length > 0) {
        // Default to first photo if nothing selected
        onSelectionChange([photos[0].photoData.id]);
      } else if (selectedIds.length > 1) {
        // Keep only the first selected item
        onSelectionChange([selectedIds[0]]);
      }
      // If exactly one item selected, keep it
    } else if (selectionMode === 'single' && selectedIds.length > 1) {
      // Keep only the first selected item when switching to single mode
      onSelectionChange([selectedIds[0]]);
    }
    // For multiple mode or single mode with 0-1 items, keep existing selection
  }, [selectionMode, photos, selectedIds, onSelectionChange]);

  // Handle empty collection (after all hooks to comply with Rules of Hooks)
  if (photos.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-center">
        <Text variant="muted">No photos to display</Text>
      </div>
    );
  }

  // Select layout component
  const LayoutComponent = layouts[layout];

  // Handle invalid layout
  if (!LayoutComponent) {
    const availableLayouts = Object.keys(layouts).join(', ');
    return (
      <ErrorState
        title="Invalid Layout"
        message={`Layout "${layout}" not found. Available layouts: ${availableLayouts}`}
        className="min-h-[400px]"
      />
    );
  }

  // Determine scroll direction based on layout
  const scrollDirection = layout === 'horizontal' ? 'horizontal' : 'vertical';

  // CSS classes for directional scrolling
  const scrollClasses = scrollDirection === 'horizontal'
    ? 'overflow-x-auto overflow-y-hidden'
    : 'overflow-y-auto overflow-x-hidden';


  // Layout component rendering (shared between reorder and normal mode)
  const renderLayout = () => (
    <LayoutComponent
      items={photos}
      itemTemplate={itemTemplate}
      selectionMode={selectionMode}
      selectedIds={selectedIds}
      zoom={zoom}
      onItemClick={handleItemClick}
      onCritiqueClick={onCritiqueClick}
      reorderContext={reorderMode ? reorderContext : undefined}
    />
  );

  // Layout content with scroll header/footer inside scrollable area (#102)
  const layoutContent = (
    <div className={cn(scrollClasses, "flex-1 p-2 min-w-0 min-h-0")}>
      {/* Scroll header - inside scroll container (#102) */}
      {effectiveScrollHeader && (
        <div className="mb-4">
          {effectiveScrollHeader}
        </div>
      )}

      {/* Wrap in SortableContext if reorder enabled */}
      {reorderMode && reorderContext ? (
        <SortableContext
          items={reorderContext.items}
          strategy={verticalListSortingStrategy}
        >
          {renderLayout()}
        </SortableContext>
      ) : (
        renderLayout()
      )}

      {/* Scroll footer - inside scroll container (#102) */}
      {effectiveScrollFooter && (
        <div className="mt-4">
          {effectiveScrollFooter}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={cn(className, "flex flex-col min-h-0 min-w-0 h-full")}
      style={{ '--zoom-factor': zoom } as React.CSSProperties}
    >
      {/* Fixed header - outside scroll container (#102) */}
      {effectiveFixedHeader && (
        <div className="flex-shrink-0">
          {effectiveFixedHeader}
        </div>
      )}

      {/* Optional description (deprecated - use scrollHeader instead) */}
      {/* Hidden when scrollHeader is provided (#102) */}
      {description && !effectiveScrollHeader && (
        <div className="flex-shrink-0 mb-6">
          <Text variant="muted">{description}</Text>
        </div>
      )}

      {/* Wrap content in DndContext if reorder enabled */}
      {reorderMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handlers.onDragStart}
          onDragOver={handlers.onDragOver}
          onDragEnd={handlers.onDragEnd}
          onDragCancel={handlers.onDragCancel}
        >
          {/* Screen reader instructions for drag and drop */}
          <div id="dnd-instructions" className="sr-only">
            To reorder photos, press space or enter to start dragging, use arrow keys to move, and press space or enter again to drop.
          </div>
          {layoutContent}
        </DndContext>
      ) : (
        layoutContent
      )}

      {/* Fixed footer - outside scroll container (#102) */}
      {effectiveFixedFooter && (
        <div className="flex-shrink-0">
          {effectiveFixedFooter}
        </div>
      )}
    </div>
  );
}
